/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import {
    call, put, select, takeLatest, fork,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import cartServiceClient  from '../../../../../../apis/cart-service-apis/cartServiceClient';
import addressBookServiceClient from '../../../../../../apis/address-book-apis/addressBookServiceClient';

import { getOrderId, getSubscription } from '../../../../Common/Common-Selectors';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { checkJWT } from '../../../../Member/ducks/Auth/Auth-Operations';
import {
    updateBillingAddress,
    updateBillingAddressFailed,
    updateBillingAddressInAddressBook,
    updateBillingAddressSuccess,
} from './BillingAddress-Actions';
import { getProfileInfo } from '../../../../Member/ducks/Common/Common-Selectors';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { getBillingAddress, getPassportSubscriptionStatus } from '../../Cart/Cart-Selectors';
import { clearOrderItemErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { loadAddressBookEntry, loadBillingAddressBookEntry } from '../../Member/AddressBookEntry/AddressBookEntry-Actions';
import { getCountryCodeFromTwoToThreeDigit } from '../../../../Checkout/helpers/countryCode';
import { getBrandConfig } from '../../App/App-Selectors';

const removeMaskForPhoneNumber = (number) => {
    let strNumber = number.toString().trim();
    strNumber = strNumber.replace(/\s/g, '').replace(/[^0-9+]/g, '');
    if (strNumber.indexOf('+') === 0 && strNumber.indexOf('1') === 1) {
        strNumber = strNumber.substring(2, strNumber.length);
    } else if (strNumber.indexOf('+') >= 0) {
        strNumber = strNumber.replace(/\s/g, '').replace(/[^0-9]/g, '');
    }
    return strNumber;
};

export function* processUpdateBillingAddress(action) {
    try {
        // show loader
        yield put(updateShowLoadingMessage());
        const { data, page } = action.payload;
        const cartId = yield select(getOrderId);

        // prepare save billing address
        if (cartId) {
            yield put(clearOrderItemErrors(
                cartId,
                'payment',
                { errType: 'clearAllFields', field: '' },
            ));

            const jwtToken = yield call(checkJWT);

            // Saving the billing on the current order cart
            const billingAddressCart = {
                address1: data.address1,
                address2: data.address2,
                address3: data.address3,
                addressId: data.addressId,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: removeMaskForPhoneNumber(data.phone),
                city: data.city,
                state: data.state,
                country: data.country ? getCountryCodeFromTwoToThreeDigit(data.country) : 'USA',
                zipCode: data.zipCode,
                email: data.email,
                confirmEmail: data.email,
                mobilePhone: removeMaskForPhoneNumber(data.mobilePhone),
            };

            const passportTermsAndConditionChecked = yield select(getPassportSubscriptionStatus);
            const subscription = yield select(getSubscription);
            const { smsOptIn, emailOptIn } = subscription;
            const updateAddressResponseCart = yield call(cartServiceClient.setBillingAddress, {
                env: {},
                jwtToken,
                cartId,
                sourceApp: 'PWA',
                customer: {
                    billingAddress: billingAddressCart,
                    cellNumber: removeMaskForPhoneNumber(data.mobilePhone),
                    emailOptIn,
                    passportTermsAndConditionChecked,
                    smsOptIn,
                },
            });

            if (updateAddressResponseCart?.data?.result === 'SUCCESS') {
                yield put(updateBillingAddressSuccess());
                if (page !== 'payment-review' && page !== 'expressCheckout') {
                    action.payload.history.push(`/checkout/review-order/${cartId}`);
                }
            } else {
                yield fork(processFeatureErrors, {
                    data: {
                        orderItemId: cartId,
                        errorFeatureType: 'payment',
                        errorValues: {
                            errorKey: 'PAYMENT_UPDATE_BILLING_ERROR',
                            errorMessage: updateAddressResponseCart?.data?.errorMessage || 'Unfortunately, there was an error.',
                        },
                    },
                });

                yield put(updateBillingAddressFailed());
            }
        } else if (page !== 'payment-review' && page !== 'expressCheckout') {
            action.payload.history.push(`/checkout/review-order/${cartId}`);
        }
        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        const cartId = yield select(getOrderId);
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'payment',
                errorValues: ex.response?.data,
            },
        });
        yield put(flagOffShowLoadingMessage());

        // log error
        mbpLogger.logError({
            action,
            appName: process.env.npm_package_name,
            module: 'Checkout-Operations.js',
            function: 'processUpdateBillingAddress',
            jsError: ex,
            message: 'Error occured while processing billing address form',
        });

        yield put(updateBillingAddressFailed());
    }
}

function* watcherBillingPageProcess() {
    yield takeLatest(updateBillingAddress().type, processUpdateBillingAddress);
}

function* workerUpdateBillingAddressInAddressBook() {
    const billingForm = yield select(getBillingAddress);
    const memberInfo = yield select(getProfileInfo);

    try {
        if (memberInfo?.contactId && billingForm.defaultBilling && ['US', 'CA', 'USA', 'CAN'].includes(billingForm.country)) {
            const jwtToken = yield call(checkJWT);
            const contactId = memberInfo?.contactId;
            const cartId = yield select(getOrderId);
            const brandConfig = yield select(getBrandConfig);

            // Save the address in address book
            if (cartId && contactId) {
                const billingAddress = {
                    brandId: brandConfig.id,
                    addressLineOne: billingForm.address1,
                    addressLineTwo: billingForm.address2,
                    addressId: billingForm.addressId,
                    firstName: billingForm.firstName,
                    lastName: billingForm.lastName,
                    employerName: billingForm.organizationName || '',
                    city: billingForm.city,
                    state: billingForm.state,
                    country: billingForm.country,
                    zip: billingForm.zipCode,
                    email: billingForm.email,
                    confirmEmail: billingForm.email,
                    phoneNumber: removeMaskForPhoneNumber(billingForm.phone),
                    mobilePhone: removeMaskForPhoneNumber(billingForm.mobilePhone),
                    isBillingAddress: true,
                };

                const userBillingResponse = yield call(
                    addressBookServiceClient.addAddressBookV2,
                    {
                        wcEnv: {},
                        jwtToken,
                        contactId,
                        addressBookEntry: billingAddress,
                    },
                );

                if (userBillingResponse?.data?.addressResponse) {
                    yield put(loadAddressBookEntry());
                    yield put(loadBillingAddressBookEntry());
                } else {
                    yield fork(processFeatureErrors, {
                        data: {
                            orderItemId: cartId,
                            errorFeatureType: 'payment',
                            errorValues: {
                                errorKey: 'UPDATE_PAYMENT_BILLING_IN_ADDRESS_BOOK_ERROR',
                                errorMessage: userBillingResponse?.data?.errorMessage || 'Unfortunately, there was an error.',
                            },
                        },
                    });

                    yield put(updateBillingAddressFailed());
                }
            }
        }
    } catch (ex) {
        const contactId = memberInfo?.contactId;
        console.log('ex', ex);
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'updateUserBillingAddress',
            jsError: ex,
            message: 'updateUserBillingAddress Failed - Error occured while save billing address',
            contactId,
        });
    }
}

function* watcherUpdateBillingAddressInAddressBook() {
    yield takeLatest(updateBillingAddressInAddressBook().type, workerUpdateBillingAddressInAddressBook);
}

const watchers = [
    fork(watcherBillingPageProcess),
    fork(watcherUpdateBillingAddressInAddressBook),
];

export {
    watchers,
};

export default {};
